import React, { forwardRef, useImperativeHandle, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { toast } from 'sonner';
import { Plus, Trash2, AlertCircle, Undo2 } from 'lucide-react';
import {
    createTimeEntry,
    updateTimeEntry,
    deleteTimeEntry,
} from '../lib/api';
import { hapticSave } from '../lib/haptics';

/**
 * DayTimeEntriesForm
 *
 * Props:
 *   selectedDay        {object}   — day object from monthInfo.days (has .date, .is_weekend, .is_red_day, .expected_hours); single-day mode only
 *   dates              {string[]} — ISO dates; bulk mode. When present, `dayEntries` holds exactly one entry
 *                                    (no per-day existing entries, no "add another") and Save fans it out to every date.
 *   dayEntries         {Array}    — array of entry objects (tempId, id?, project_id, deviation_type_id, hours, activity, activity_id, overtime_type, notes)
 *   setDayEntries      {function} — setState for dayEntries
 *   projects           {Array}    — active project list
 *   deviationTypes     {Array}    — active deviation types
 *   userBalances       {object}   — { flex, vacation, advanceVacation, advanceVacationUsed }
 *   lastProjectId      {string}   — last selected project id for prefill
 *   setLastProjectId   {function} — callback to persist last project selection
 *   onSaved            {function} — called after successful save; parent uses to refresh data and mark tab clean
 * TODO: wire onSaved to parent fetchData() + markDirty(false) for the Time tab
 */
const DayTimeEntriesForm = forwardRef(function DayTimeEntriesForm({
    selectedDay,
    dates,
    dayEntries,
    setDayEntries,
    projects,
    deviationTypes,
    userBalances,
    lastProjectId,
    setLastProjectId,
    onSaved,
}, ref) {
    const { t } = useTranslation();
    const [saving, setSaving] = useState(false);
    const isBulkMode = Array.isArray(dates) && dates.length > 0;

    // Retry set for bulk mode — starts as the full selection, narrows to only
    // the failed dates after a partial failure so a retry doesn't resubmit
    // (duplicate) the dates that already succeeded. Reset whenever the
    // parent hands us a new selection.
    const [bulkDates, setBulkDates] = useState(dates || []);
    useEffect(() => {
        if (isBulkMode) setBulkDates(dates);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dates]);

    const dateStr = selectedDay?.date;
    const isWorkingDay = selectedDay && !selectedDay.is_weekend && !selectedDay.is_red_day;

    // ── Helper: isRedDay ────────────────────────────────────────────────────────
    const isRedDay = (dateStr) => {
        if (selectedDay && selectedDay.date === dateStr) return !!selectedDay.is_red_day;
        const d = new Date(dateStr + 'T00:00:00');
        return d.getDay() === 0;
    };

    // ── Helper: project policy ──────────────────────────────────────────────────
    const getProjectPolicy = (projectId) => {
        const p = projects?.find(x => x.id === projectId);
        if (!p) return { kind: 'unknown', allowFlex: true, allowOT: true };
        const isInternal = !p.billable;
        return {
            kind: isInternal ? 'internal' : 'client',
            allowFlex: isInternal,
            allowOT: !isInternal,
        };
    };

    const getExtraOptions = ({ dateStr, projectId }) => {
        const red = isRedDay(dateStr);
        const policy = getProjectPolicy(projectId);
        const options = [{ value: '', label: t('timesheet.entry_type.regular') }];
        // flex_save removed from new-entry options — overtime banking is now set
        // at the day level via the flex election toggle in the day panel.
        // Existing entries with overtime_type === 'flex_save' are still displayed
        // correctly (the pill uses the flex-deposit CSS class); they just cannot
        // be newly created via this dropdown.
        if (policy.allowOT) {
            options.push({ value: 'ot1', label: t('timesheet.entry_type.ot_standard') });
            options.push({ value: 'ot2', label: t('timesheet.entry_type.ot_special') });
            if (red) options.push({ value: 'ot_red', label: t('timesheet.entry_type.ot_special') });
        }
        return options;
    };

    // ── Helper: deviation balance info ─────────────────────────────────────────
    const getDeviationBalanceInfo = (deviationTypeId) => {
        if (!deviationTypeId) return null;
        const deviation = deviationTypes.find(d => d.id === deviationTypeId);
        if (!deviation) return null;
        const name = deviation.name.toLowerCase();
        if (name.includes('flex')) {
            return { type: 'flex', label: t('profile.flex_balance_title'), balance: userBalances.flex, unit: t('common.hours') };
        }
        if (name.includes('vacation') || name.includes('semester')) {
            const available = userBalances.vacation + (userBalances.advanceVacation - userBalances.advanceVacationUsed);
            return {
                type: 'vacation',
                label: t('profile.vacation_days_title'),
                balance: userBalances.vacation,
                advanceAvailable: userBalances.advanceVacation - userBalances.advanceVacationUsed,
                totalAvailable: available,
                unit: t('common.days_unit'),
            };
        }
        return null;
    };

    // ── Entry mutation helpers ──────────────────────────────────────────────────
    const addEntry = () => {
        setDayEntries(prev => [...prev, {
            tempId: Date.now(),
            project_id: lastProjectId || '',
            activity: '',
            activity_id: '',
            hours: '',
            deviation_type_id: '',
            overtime_type: '',
            notes: '',
        }]);
    };

    const removeEntry = (tempId) => {
        setDayEntries(prev => prev.map(e => {
            if (e.tempId !== tempId) return e;
            // Existing row (has a real id) — soft-mark for deletion so Save
            // can fire a DELETE. Mirrors DayMileageForm / DayReceiptForm.
            if (e.id) return { ...e, _markDeleted: true };
            // New row — just drop from state.
            return null;
        }).filter(Boolean));
    };

    const undoRemove = (tempId) => {
        setDayEntries(prev => prev.map(e =>
            e.tempId === tempId ? { ...e, _markDeleted: false } : e
        ));
    };

    const updateEntry = (tempId, field, value) => {
        setDayEntries(prev => prev.map(e =>
            e.tempId === tempId ? { ...e, [field]: value } : e
        ));
        if (field === 'project_id' && value) {
            setLastProjectId(value);
            localStorage.setItem('lastSelectedProject', value);
        }
    };

    const updateEntryFields = (tempId, updates) => {
        setDayEntries(prev => prev.map(e =>
            e.tempId === tempId ? { ...e, ...updates } : e
        ));
        if (updates.project_id) {
            setLastProjectId(updates.project_id);
            localStorage.setItem('lastSelectedProject', updates.project_id);
        }
    };

    // ── Totals & overtime summary ───────────────────────────────────────────────
    // Overtime/red-day accounting is inherently per-day (it compares against
    // one day's expected_hours) and doesn't generalize to a bulk selection of
    // N different dates, each with their own weekend/red-day/expected_hours
    // status — bulk mode skips it entirely (matching the old bulk dialog,
    // which never had an overtime selector either), same as bulk Mileage/
    // Receipt/Allowance never offered project-policy-driven fields that only
    // make sense for one specific day.
    const dayTotalHours = dayEntries.reduce((sum, e) => sum + (parseFloat(e.hours) || 0), 0);
    // Use expected_hours from the day object (set by backend using company base hours).
    // Falls back to 8 only if selectedDay is not yet available.
    const dayRegularBase = isBulkMode ? 0 : (selectedDay?.is_weekend || isRedDay(selectedDay?.date))
        ? 0
        : (selectedDay?.expected_hours ?? 8);
    const dayExtraHoursTotal = isBulkMode ? 0 : Math.max(0, dayTotalHours - dayRegularBase);

    // Under the auto-flex model, flex banking is automatic — no flex_save lane needed.
    // Only explicit OT types on billable projects are accounted for here.
    const ot1SelectedHours = dayEntries
        .filter(e => e.project_id && e.overtime_type === 'ot1')
        .reduce((sum, e) => sum + (parseFloat(e.hours) || 0), 0);
    const ot2SelectedHours = dayEntries
        .filter(e => e.project_id && e.overtime_type === 'ot2')
        .reduce((sum, e) => sum + (parseFloat(e.hours) || 0), 0);
    const otRedSelectedHours = dayEntries
        .filter(e => e.project_id && e.overtime_type === 'ot_red')
        .reduce((sum, e) => sum + (parseFloat(e.hours) || 0), 0);

    let remainingExtra = dayExtraHoursTotal;
    const ot1ToPay = Math.min(remainingExtra, ot1SelectedHours); remainingExtra -= ot1ToPay;
    const ot2ToPay = Math.min(remainingExtra, ot2SelectedHours); remainingExtra -= ot2ToPay;
    const otRedToPay = Math.min(remainingExtra, otRedSelectedHours); remainingExtra -= otRedToPay;
    const unassignedExtra = Math.max(0, remainingExtra);

    // ── Save handler ────────────────────────────────────────────────────────────
    const saveDayEntries = async () => {
        const totalHours = dayEntries.reduce((sum, e) => sum + parseFloat(e.hours || 0), 0);
        if (totalHours > 24) {
            toast.error(t('error.timesheet.max_hours_exceeded'));
            return;
        }

        for (const entry of dayEntries) {
            if (!entry.project_id && !entry.deviation_type_id) {
                toast.error(t('timesheet.bulk.no_project_error'));
                return;
            }
            const hrs = parseFloat(entry.hours || 0);
            if (!entry.id) {
                if (hrs > 0 && hrs < 1) { toast.error(t('error.timesheet.min_hours_invalid')); return; }
            } else {
                if (hrs > 0 && hrs < 1) { toast.error(t('error.timesheet.min_hours_invalid')); return; }
            }
        }

        for (const entry of dayEntries) {
            if (entry.deviation_type_id && entry.overtime_type) {
                toast.error(t('error.timesheet.cannot_edit_entry'));
                return;
            }
        }

        for (const entry of dayEntries) {
            const hrs = parseFloat(entry.hours || 0);
            if (hrs === 0) continue;
            if (entry.deviation_type_id) {
                const balanceInfo = getDeviationBalanceInfo(entry.deviation_type_id);
                if (balanceInfo?.type === 'vacation') {
                    // In bulk mode this one entry is applied to every selected
                    // date, so it draws on the vacation balance once per date
                    // — not once total (matching the old bulk dialog's
                    // `daysNeeded = selectedDays.length` check).
                    // bulkDates (not the original dates prop) so a retry after
                    // a partial failure only counts the dates still pending.
                    const daysNeeded = isBulkMode ? (hrs / 8) * bulkDates.length : hrs / 8;
                    if (daysNeeded > balanceInfo.totalAvailable) {
                        toast.error(t('timesheet.bulk.vacation_insufficient_error'));
                        return;
                    }
                    if (balanceInfo.balance <= 0) {
                        const confirmed = window.confirm(
                            t('timesheet.bulk_dialog.insufficient_days_error')
                        );
                        if (!confirmed) return;
                    }
                }
            }
        }

        // Phase 23 (PRD 12.2): an activity is required on any entry whose
        // project defines at least one — mirrors backend/utils/activities.py's
        // resolve_activity exactly so the user hits this toast instead of a
        // 400 from the server. A project with no activities never reaches
        // this check (projectActivities.length > 0 guards it, same condition
        // the render block above uses to decide select-vs-free-text), and a
        // deviation-only row (no project_id) is skipped the same way.
        //
        // 23.2-R: this must only fire for rows the save loop below will
        // actually submit — mirroring its two skip conditions exactly
        // (`entry.id && entry._markDeleted` for a row being deleted, and a
        // brand-new/no-id row with no positive hours, which the save loop
        // drops as "nothing to create"). Without this, deleting an old
        // activity-less row, or leaving a blank extra row, threw an
        // "activity required" toast that named no row and blocked the save
        // entirely — on a row the user was trying to remove, not submit.
        for (const entry of dayEntries) {
            if (!entry.project_id) continue;
            if (entry.id && entry._markDeleted) continue;
            if (!entry.id && (parseFloat(entry.hours || 0) <= 0)) continue;
            const selectedProject = projects?.find(p => p.id === entry.project_id);
            const projectActivities = selectedProject?.activities || [];
            if (projectActivities.length > 0 && !entry.activity_id) {
                toast.error(t('error.activities.required'));
                return;
            }
        }

        setSaving(true);

        if (isBulkMode) {
            // Bulk mode: there is exactly one entry (no existing rows, no
            // add-another), fanned out to every selected date as its own
            // independent create — same Promise.allSettled-and-reconcile
            // shape as single-day, just N creates from one entry instead of
            // N different rows for one day.
            const entry = dayEntries[0];
            const hrs = parseFloat(entry?.hours || 0);
            if (!entry || hrs <= 0) {
                setSaving(false);
                hapticSave();
                onSaved();
                return;
            }
            const results = await Promise.allSettled(bulkDates.map(d => createTimeEntry({
                date: d,
                project_id: entry.project_id || null,
                activity: entry.activity || null,
                activity_id: entry.activity_id || null,
                hours: hrs,
                deviation_type_id: entry.deviation_type_id || null,
                overtime_type: null,
                notes: entry.notes || null,
            })));
            const failedDates = bulkDates.filter((d, i) => results[i].status === 'rejected');
            setSaving(false);
            if (failedDates.length === 0) {
                hapticSave();
                // Reset to a fresh blank entry — this batch is fully saved,
                // so leaving the old values in place would invite an
                // accidental duplicate submit on the next Save Day.
                setDayEntries([{
                    tempId: Date.now(),
                    project_id: '', activity: '', activity_id: '', hours: '',
                    deviation_type_id: '', overtime_type: '', notes: '',
                }]);
                onSaved();
            } else {
                // Narrow the retry set to only the dates that actually
                // failed — the ones that succeeded must not be resubmitted.
                setBulkDates(failedDates);
                toast.error(t('timesheet.bulk.save_error'));
                throw new Error('Bulk time entries save failed');
            }
            return;
        }

        // Build one op per row — each row's create/update/delete is
        // independent of every other row, so they're safe to run
        // concurrently via Promise.allSettled (mirrors DayMileageForm's
        // handleSave). This replaces the old sequential await-loop, which
        // aborted on the first failure and silently lost track of any
        // earlier ops that had already succeeded — a retry would then
        // redo (or re-delete) rows the server already processed.
        const ops = [];
        for (const entry of dayEntries) {
            if (entry.id && entry._markDeleted) {
                ops.push({ tempId: entry.tempId, kind: 'delete', promise: deleteTimeEntry(entry.id) });
                continue;
            }
            if (entry.id && !entry._markDeleted) {
                const hrs = parseFloat(entry.hours || 0);
                ops.push({
                    tempId: entry.tempId,
                    kind: 'update',
                    zeroHours: hrs === 0,
                    promise: updateTimeEntry(entry.id, {
                        date: selectedDay.date,
                        project_id: entry.project_id || null,
                        activity: entry.activity || null,
                        activity_id: entry.activity_id || null,
                        hours: hrs,
                        deviation_type_id: entry.deviation_type_id || null,
                        overtime_type: entry.overtime_type || null,
                        notes: entry.notes || null,
                    }),
                });
                continue;
            }
            // New row — skip entirely empty ones (nothing to create)
            const hrs = parseFloat(entry.hours || 0);
            if (hrs <= 0) continue;
            ops.push({
                tempId: entry.tempId,
                kind: 'create',
                promise: createTimeEntry({
                    date: selectedDay.date,
                    project_id: entry.project_id || null,
                    activity: entry.activity || null,
                    activity_id: entry.activity_id || null,
                    hours: hrs,
                    deviation_type_id: entry.deviation_type_id || null,
                    overtime_type: entry.overtime_type || null,
                    notes: entry.notes || null,
                }),
            });
        }

        if (ops.length === 0) {
            setSaving(false);
            hapticSave();
            onSaved();
            return;
        }

        const results = await Promise.allSettled(ops.map(o => o.promise));

        const deletedTempIds = new Set();
        const zeroHoursDeletedTempIds = new Set();
        const newIdByTempId = {};
        let failedCount = 0;

        results.forEach((result, i) => {
            const op = ops[i];
            if (result.status === 'rejected') {
                failedCount += 1;
                return;
            }
            if (op.kind === 'delete') {
                deletedTempIds.add(op.tempId);
            } else if (op.kind === 'update') {
                if (op.zeroHours) zeroHoursDeletedTempIds.add(op.tempId);
            } else if (op.kind === 'create') {
                const newId = result.value?.data?.id;
                if (newId) newIdByTempId[op.tempId] = newId;
            }
        });

        // Reconcile local state with backend truth for every op that
        // succeeded — rows whose op failed are left untouched (including
        // their in-progress edits) so the dialog stays open with surviving
        // edits intact and a retry only re-attempts what actually failed.
        setDayEntries(prev => prev
            .filter(e => !deletedTempIds.has(e.tempId) && !zeroHoursDeletedTempIds.has(e.tempId))
            .map(e => newIdByTempId[e.tempId] ? { ...e, id: newIdByTempId[e.tempId] } : e)
        );

        setSaving(false);

        if (failedCount === 0) {
            hapticSave();
            onSaved();
        } else {
            toast.error(t('timesheet.bulk.save_error'));
            throw new Error('Time entries save failed');
        }
    };

    // Expose save function to parent via ref (used by Save Day orchestrator)
    useImperativeHandle(ref, () => ({ save: saveDayEntries }));

    // ── Render ──────────────────────────────────────────────────────────────────
    return (
        <div className="space-y-4 py-2">
            {selectedDay && (selectedDay.is_weekend || selectedDay.is_red_day) && (
                <div className="flex items-start gap-2 p-3 bg-muted border border-border">
                    <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    <p className="text-xs">
                        {selectedDay.is_red_day ? t('timesheet.legend.red_day') : t('timesheet.legend.weekend')}.
                        {t('timesheet.day_form.weekend_red_day_hint')}
                    </p>
                </div>
            )}

            {dayEntries.map((entry, index) => {
                const isDeleted = entry._markDeleted;
                // Show trash on existing rows always (so you can delete the last one too).
                // On new rows, only show when there's more than one to prevent reaching
                // zero rows (the add-another button brings back a fresh row anyway).
                const showTrash = entry.id ? !isDeleted : dayEntries.length > 1;
                return (
                <div
                    key={entry.tempId}
                    className={`border border-border p-4 space-y-4 transition-opacity ${isDeleted ? 'opacity-40' : ''}`}
                >
                    <div className="flex justify-between items-center">
                        <span className={`text-xs uppercase tracking-widest text-muted-foreground ${isDeleted ? 'line-through' : ''}`}>
                            {t('expenses.mileage.entry_label', { num: index + 1 })}
                        </span>
                        {isDeleted ? (
                            <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => undoRemove(entry.tempId)}
                                className="min-h-[44px] min-w-[44px] p-0"
                                data-testid={`undo-remove-entry-${index}`}
                                aria-label={t('common.undo')}
                            >
                                <Undo2 className="w-4 h-4" />
                            </Button>
                        ) : showTrash && (
                            <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => removeEntry(entry.tempId)}
                                className="min-h-[44px] min-w-[44px] p-0 text-destructive"
                                data-testid={`remove-entry-${index}`}
                                aria-label={t('common.delete')}
                            >
                                <Trash2 className="w-4 h-4" />
                            </Button>
                        )}
                    </div>

                    {!entry.project_id && !entry.deviation_type_id && (
                        <p className="text-xs text-muted-foreground">{t('timesheet.day_form.project_or_deviation_required')}</p>
                    )}
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <Label className="label-uppercase mb-2 block">{t('common.project')}</Label>
                            <select
                                value={entry.project_id}
                                onChange={(e) => {
                                    const val = e.target.value;
                                    if (val) {
                                        const policy = getProjectPolicy(val);
                                        const red = isRedDay(selectedDay?.date);
                                        let defaultOvertime = '';
                                        // flex_save is no longer a valid new-entry option; internal projects
                                        // default to regular (empty) — overtime banking is via the day-level
                                        // flex election toggle.
                                        if (policy.kind === 'client') defaultOvertime = red ? 'ot_red' : 'ot1';
                                        const allowed = getExtraOptions({ dateStr: selectedDay?.date, projectId: val }).map(o => o.value);
                                        const nextOvertime = allowed.includes(entry.overtime_type) ? entry.overtime_type : defaultOvertime;
                                        updateEntryFields(entry.tempId, {
                                            project_id: val,
                                            deviation_type_id: '',
                                            overtime_type: nextOvertime,
                                            activity: '',
                                            activity_id: '',
                                        });
                                    } else {
                                        updateEntry(entry.tempId, 'project_id', val);
                                    }
                                }}
                                className="w-full h-10 px-3 border border-input bg-background text-sm"
                                data-testid={`project-select-${index}`}
                            >
                                <option value="">{t('timesheet.bulk_dialog.project_placeholder')}</option>
                                {projects.map(p => (
                                    <option key={p.id} value={p.id}>
                                        {p.project_code ? `${p.project_code} - ${p.name}` : p.name}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div>
                            <Label className="label-uppercase mb-2 block">{t('timesheet.bulk_dialog.deviation_label')}</Label>
                            <select
                                value={entry.deviation_type_id}
                                onChange={(e) => {
                                    const val = e.target.value;
                                    if (val) {
                                        updateEntryFields(entry.tempId, {
                                            deviation_type_id: val,
                                            project_id: '',
                                            activity: '',
                                            activity_id: '',
                                            overtime_type: '',
                                        });
                                    } else {
                                        updateEntry(entry.tempId, 'deviation_type_id', val);
                                    }
                                }}
                                className="w-full h-10 px-3 border border-input bg-background text-sm"
                                data-testid={`deviation-select-${index}`}
                            >
                                <option value="">{t('timesheet.bulk_dialog.deviation_placeholder')}</option>
                                {deviationTypes.map(d => (
                                    <option key={d.id} value={d.id}>{d.name}</option>
                                ))}
                            </select>
                            {entry.deviation_type_id && (() => {
                                const balanceInfo = getDeviationBalanceInfo(entry.deviation_type_id);
                                if (!balanceInfo) return null;
                                if (balanceInfo.type === 'vacation') {
                                    const hoursEntered = parseFloat(entry.hours) || 0;
                                    const daysToDeduct = hoursEntered / 8;
                                    return (
                                        <div className="mt-2 p-2 bg-muted rounded text-xs">
                                            <div className="flex justify-between">
                                                <span>{t('timesheet.bulk_dialog.paid_vacation_label')}</span>
                                                <span className="font-mono font-bold">{balanceInfo.balance} {t('common.days_unit')}</span>
                                            </div>
                                            <div className="flex justify-between">
                                                <span>{t('timesheet.bulk_dialog.advance_available_label')}</span>
                                                <span className="font-mono font-bold">{balanceInfo.advanceAvailable} {t('common.days_unit')}</span>
                                            </div>
                                            <div className="flex justify-between border-t border-border mt-1 pt-1">
                                                <span>{t('timesheet.bulk_dialog.total_available_label')}</span>
                                                <span className="font-mono font-bold">{balanceInfo.totalAvailable} {t('common.days_unit')}</span>
                                            </div>
                                            {hoursEntered > 0 && (
                                                <div className="flex justify-between border-t border-amber-300 mt-2 pt-2 text-amber-700 bg-amber-50 -mx-2 -mb-2 px-2 py-1.5 rounded-b">
                                                    <span className="font-medium">{t('timesheet.bulk_dialog.paid_vacation_label')}</span>
                                                    <span className="font-mono font-bold">
                                                        {daysToDeduct === 1 ? t('timesheet.day_form.vacation_deduct_one') :
                                                         daysToDeduct === 0.5 ? t('timesheet.day_form.vacation_deduct_half') :
                                                         t('timesheet.day_form.vacation_deduct_other', { days: daysToDeduct.toFixed(2) })}
                                                    </span>
                                                </div>
                                            )}
                                        </div>
                                    );
                                }
                                return (
                                    <div className="mt-2 p-2 bg-muted rounded text-xs">
                                        <div className="flex justify-between">
                                            <span>{balanceInfo.label}:</span>
                                            <span className="font-mono font-bold">{balanceInfo.balance} {balanceInfo.unit}</span>
                                        </div>
                                    </div>
                                );
                            })()}
                        </div>
                    </div>

                    {entry.project_id && (() => {
                        const selectedProject = projects?.find(p => p.id === entry.project_id);
                        const projectActivities = selectedProject?.activities || [];

                        if (projectActivities.length > 0) {
                            return (
                                <div>
                                    <Label className="label-uppercase mb-2 block">
                                        {t('timesheet.bulk_dialog.activity_label')} <span className="text-destructive">*</span>
                                    </Label>
                                    <select
                                        value={entry.activity_id || ''}
                                        onChange={(e) => {
                                            const actId = e.target.value;
                                            const act = projectActivities.find(a => a.id === actId);
                                            updateEntryFields(entry.tempId, {
                                                activity_id: actId,
                                                activity: act ? act.name : '',
                                            });
                                        }}
                                        className="w-full border border-input rounded-md px-3 py-2 text-sm bg-background focus:outline-none focus:ring-1 focus:ring-ring"
                                        data-testid={`activity-select-${index}`}
                                        aria-required="true"
                                    >
                                        <option value="">{t('timesheet.bulk_dialog.activity_placeholder')}</option>
                                        {projectActivities.map(act => (
                                            <option key={act.id} value={act.id}>
                                                {act.name}{act.activity_code ? ` (${act.activity_code})` : ''}
                                            </option>
                                        ))}
                                    </select>
                                    {/* Mirrors the project_or_deviation_required inline hint below —
                                        names the exact offending row instead of leaving the save-time
                                        toast to name none of them.
                                        23.R4: only shown for a row Save will actually validate — reuses
                                        the exact skip conditions from the save-time loop above (a row
                                        pending deletion, or a blank new row with no positive hours,
                                        is never sent, so it must never show a rule that won't apply). */}
                                    {!entry.activity_id &&
                                        !(entry.id && entry._markDeleted) &&
                                        !(!entry.id && parseFloat(entry.hours || 0) <= 0) && (
                                        <p className="text-xs text-destructive mt-1">{t('error.activities.required')}</p>
                                    )}
                                </div>
                            );
                        }

                        return (
                            <div>
                                <Label className="label-uppercase mb-2 block">{t('timesheet.bulk_dialog.activity_label')}</Label>
                                <Input
                                    value={entry.activity || ''}
                                    onChange={(e) => updateEntry(entry.tempId, 'activity', e.target.value)}
                                    placeholder={t('timesheet.bulk_dialog.activity_placeholder')}
                                    data-testid={`activity-input-${index}`}
                                />
                            </div>
                        );
                    })()}

                    <div className="grid grid-cols-3 gap-4">
                        <div>
                            <Label className="label-uppercase mb-2 block">{t('common.hours')}</Label>
                            {(() => {
                                const balanceInfo = getDeviationBalanceInfo(entry.deviation_type_id);
                                const isVacation = balanceInfo?.type === 'vacation';
                                const maxHours = isVacation ? 8 : 24;
                                return (
                                    <>
                                        <Input
                                            type="number"
                                            min="0"
                                            max={maxHours}
                                            step="0.5"
                                            value={entry.hours}
                                            onChange={(e) => {
                                                let val = parseFloat(e.target.value) || 0;
                                                if (isVacation && val > 8) val = 8;
                                                updateEntry(entry.tempId, 'hours', val);
                                            }}
                                            data-testid={`hours-input-${index}`}
                                        />
                                        {isVacation && (
                                            <p className="text-xs text-muted-foreground mt-1">{t('timesheet.bulk_dialog.paid_vacation_label')}</p>
                                        )}
                                    </>
                                );
                            })()}
                        </div>

                        {entry.project_id && dayExtraHoursTotal > 0 && getProjectPolicy(entry.project_id).allowOT && (
                            <div className="col-span-2">
                                <Label className="label-uppercase mb-2 block">{t('timesheet.legend.overtime')}</Label>
                                <select
                                    value={entry.overtime_type || ''}
                                    onChange={(e) => updateEntry(entry.tempId, 'overtime_type', e.target.value)}
                                    className="w-full h-10 px-3 border border-input bg-background text-sm"
                                    data-testid={`overtime-select-${index}`}
                                >
                                    {getExtraOptions({ dateStr: selectedDay?.date, projectId: entry.project_id }).map(opt => (
                                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                                    ))}
                                </select>
                            </div>
                        )}
                    </div>

                    <div>
                        <Label className="label-uppercase mb-2 block">{t('timesheet.bulk_dialog.notes_label')}</Label>
                        <Input
                            value={entry.notes || ''}
                            onChange={(e) => updateEntry(entry.tempId, 'notes', e.target.value)}
                            placeholder={t('timesheet.bulk_dialog.notes_placeholder')}
                            data-testid={`notes-input-${index}`}
                        />
                    </div>
                </div>
                );
            })}

            {/* Overtime summary */}
            {dayExtraHoursTotal > 0 && (
                <div className="mt-2 space-y-1">
                    {ot1ToPay > 0 && (
                        <p className="text-xs text-blue-600 font-medium">
                            {t('timesheet.entry_type.ot_standard')}: {ot1ToPay.toFixed(1)}h
                        </p>
                    )}
                    {ot2ToPay > 0 && (
                        <p className="text-xs text-blue-600 font-medium">
                            {t('timesheet.entry_type.ot_special')}: {ot2ToPay.toFixed(1)}h
                        </p>
                    )}
                    {otRedToPay > 0 && (
                        <p className="text-xs text-blue-600 font-medium">
                            {t('timesheet.legend.red_day')}: {otRedToPay.toFixed(1)}h
                        </p>
                    )}
                    {unassignedExtra > 0 && dayEntries.some(e =>
                        e.project_id && !e.deviation_type_id &&
                        getProjectPolicy(e.project_id).allowOT &&
                        !['ot1', 'ot2', 'ot_red'].includes(e.overtime_type)
                    ) && (
                        <p className="text-xs text-amber-700">
                            {unassignedExtra.toFixed(1)}h {t('timesheet.day_form.unassigned_extra_hours')}
                        </p>
                    )}
                </div>
            )}

            <div className="flex justify-between items-center p-4 bg-muted">
                <span className="text-sm uppercase tracking-widest">
                    {isBulkMode ? t('timesheet.header.hours_per_day_label') : t('timesheet.header.hours_label')}
                </span>
                <span className="font-mono text-xl font-bold">{dayTotalHours}h</span>
            </div>

            {!isBulkMode && (
                <Button
                    variant="outline"
                    className="w-full uppercase tracking-wider text-xs"
                    onClick={addEntry}
                    data-testid="add-entry-btn"
                >
                    <Plus className="w-4 h-4 mr-2" />
                    {t('timesheet.add_another_entry')}
                </Button>
            )}

        </div>
    );
});

export default DayTimeEntriesForm;
